import React from 'react'
import { Flex, Tag, Tooltip } from 'antd'
import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  CreditCardOutlined,
  FileExclamationOutlined,
  GlobalOutlined,
  LineChartOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
  TransactionOutlined
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { MatchClassification } from '../types'

const DEFAULT_COLOR = '#999'
const DEFAULT_TAG_COLOR = 'default'
const DefaultIcon = QuestionCircleOutlined

type ClassificationIconType = React.ComponentType<{ style?: React.CSSProperties }>

interface ClassificationMeta {
  color: string
  tagColor: string
  icon: ClassificationIconType
}

// One entry per classification instead of three parallel maps — adding a classification
// touches a single place. Partial, not Record: the backend can send a value we don't know
// about yet, and falling through to a default beats crashing the grid.
const CLASSIFICATION_META: Partial<Record<MatchClassification, ClassificationMeta>> = {
  [MatchClassification.VALID]: { color: '#52c41a', tagColor: 'success', icon: CheckCircleOutlined },
  [MatchClassification.NO_COUNTERPART]: { color: '#f5222d', tagColor: 'error', icon: FileExclamationOutlined },
  [MatchClassification.TIMING_DIFFERENCE]: { color: '#faad14', tagColor: 'gold', icon: ClockCircleOutlined },
  [MatchClassification.SUM_DIFFERENCE]: { color: '#fa8c16', tagColor: 'volcano', icon: SwapOutlined },
  [MatchClassification.CURRENCY_DIFFERENCE]: { color: '#13c2c2', tagColor: 'cyan', icon: TransactionOutlined },
  [MatchClassification.INVALID_INCOME_STATEMENT_ACCOUNT]: {
    color: '#f5222d',
    tagColor: 'error',
    icon: LineChartOutlined
  },
  [MatchClassification.INVALID_ASSETS_ACCOUNT]: { color: '#f5222d', tagColor: 'error', icon: BankOutlined },
  [MatchClassification.INVALID_LIABILITIES_ACCOUNT]: { color: '#f5222d', tagColor: 'error', icon: CreditCardOutlined },
  [MatchClassification.INVALID_ACCOUNT]: { color: '#f5222d', tagColor: 'error', icon: QuestionCircleOutlined },
  [MatchClassification.EXTERNAL_ON_IC_ACCOUNT]: { color: '#f5222d', tagColor: 'error', icon: GlobalOutlined },
  [MatchClassification.DUPLICATE_REFERENCE]: { color: '#f5222d', tagColor: 'error', icon: CopyOutlined }
}

export const getClassificationColor = (classification: MatchClassification) =>
  CLASSIFICATION_META[classification]?.color ?? DEFAULT_COLOR

const getClassificationIcon = (classification: MatchClassification): ClassificationIconType =>
  CLASSIFICATION_META[classification]?.icon ?? DefaultIcon

interface ClassificationDotProps {
  classification: MatchClassification
}

export const ClassificationDot: React.FC<ClassificationDotProps> = ({ classification }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: getClassificationColor(classification),
      marginRight: 8
    }}
  />
)

interface ClassificationTagProps {
  classification: MatchClassification
}

const ClassificationTag: React.FC<ClassificationTagProps> = ({ classification }) => {
  const { t } = useTranslation()
  const Icon = getClassificationIcon(classification)

  return (
    <Tag color={CLASSIFICATION_META[classification]?.tagColor ?? DEFAULT_TAG_COLOR} icon={<Icon />}>
      {t(`icReconciliation:classification-${classification}`, classification)}
    </Tag>
  )
}

interface ClassificationTagsProps {
  classifications: MatchClassification[]
}

export const ClassificationTags: React.FC<ClassificationTagsProps> = ({ classifications }) => (
  <Flex gap={4} wrap>
    {classifications.map(classification => (
      <ClassificationTag key={classification} classification={classification} />
    ))}
  </Flex>
)

interface ClassificationIconProps {
  classification: MatchClassification
}

export const ClassificationIcon: React.FC<ClassificationIconProps> = ({ classification }) => {
  const { t } = useTranslation()
  const Icon = getClassificationIcon(classification)
  const label = t(`icReconciliation:classification-${classification}`, classification)
  const color = getClassificationColor(classification)

  return (
    <Tooltip title={label}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '30%',
          backgroundColor: `${color}1a`
        }}
      >
        <Icon style={{ color, fontSize: 13 }} />
      </span>
    </Tooltip>
  )
}

interface ClassificationIconsProps {
  classifications: MatchClassification[]
}

export const ClassificationIcons: React.FC<ClassificationIconsProps> = ({ classifications }) => (
  <Flex gap={6} align="center" style={{ height: '100%' }}>
    {classifications.map(classification => (
      <ClassificationIcon key={classification} classification={classification} />
    ))}
  </Flex>
)

export default ClassificationTag
